import { useState, useEffect } from 'react';
import { Bell, Check, X, UtensilsCrossed, Pencil, Banknote, CreditCard } from 'lucide-react';
import { useConfirmOrder, useSkipOrder, useUpdateOrderItems } from '../hooks/useQueueQuery';
import { ModifyOrderModal } from './ModifyOrderModal';
import type { Order, OrderItem } from '../types';

interface CurrentOrderProps {
  order: Order | null;
  shopId: string;
}

export function CurrentOrder({ order, shopId }: CurrentOrderProps) {
  const confirmOrderMutation = useConfirmOrder(shopId);
  const skipOrderMutation = useSkipOrder(shopId);
  const updateOrderMutation = useUpdateOrderItems(shopId);
  const [error, setError] = useState<string | null>(null);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update timer every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleConfirm = async () => {
    if (!order) return;
    try {
      setError(null);
      await confirmOrderMutation.mutateAsync(order.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل تأكيد الطلب');
    }
  };

  const handleSkip = async () => {
    if (!order) return;
    try {
      setError(null);
      await skipOrderMutation.mutateAsync(order.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل تخطي الطلب');
    }
  };

  const handleModifySave = async (items: OrderItem[]) => {
    if (!order) return;
    try {
      setError(null);
      await updateOrderMutation.mutateAsync({ orderId: order.id, items });
      setShowModifyModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل تعديل الطلب');
    }
  };

  if (!order) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8">
        <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-surface-light">
          <Bell className="h-12 w-12 text-gray-500" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">لا يوجد عميل حالي</h2>
        <p className="text-gray-400 text-center max-w-sm">
          اضغط على "استدعاء التالي" لاستدعاء العميل القادم في الطابور
        </p>
      </div>
    );
  }

  // Calculate time since called (updates every second via currentTime state)
  const getElapsedTime = () => {
    if (!order?.calledAt) return '0:00';
    const called = new Date(order.calledAt);
    const diffMs = currentTime - called.getTime();
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isLoading = confirmOrderMutation.isPending || skipOrderMutation.isPending || updateOrderMutation.isPending;

  return (
    <div className="flex h-full flex-col bg-surface">
      {/* Compact Header - Queue Number & Key Info */}
      <div className="shrink-0 border-b border-border bg-surface-light px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          {/* Queue Number - Compact */}
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-xl border-2 border-primary-500 bg-primary-500/10">
              <span className="text-3xl font-bold text-primary-500">
                {order.queueNumber}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-400">رقم الطلب</p>
              {order.customerName && (
                <p className="text-lg font-semibold text-white">{order.customerName}</p>
              )}
              <p className="text-sm text-gray-400">
                منذ <span className="font-mono text-white">{getElapsedTime()}</span>
              </p>
            </div>
          </div>

          {/* Total - Prominent */}
          <div className="text-left">
            <p className="text-sm text-gray-400">المجموع</p>
            <p className="text-3xl font-bold text-primary-500">
              {parseInt(order.totalAmount).toLocaleString('ar-IQ')}
              <span className="text-lg text-gray-400 mr-1">د.ع</span>
            </p>
          </div>
        </div>

        {/* Status & Payment Row */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-lg bg-amber-500/20 px-3 py-1.5 border border-amber-500/30">
            <Bell className="h-4 w-4 text-amber-400 animate-pulse" />
            <span className="text-sm font-medium text-amber-400">في انتظار التأكيد</span>
          </div>
          <div className={`flex items-center gap-2 rounded-lg px-3 py-1.5 border ${
            order.paymentMethod === 'card'
              ? 'bg-green-500/20 border-green-500/30'
              : 'bg-gray-500/20 border-gray-500/30'
          }`}>
            {order.paymentMethod === 'card' ? (
              <>
                <CreditCard className="h-4 w-4 text-green-400" />
                <span className="text-sm font-medium text-green-400">مدفوع</span>
              </>
            ) : (
              <>
                <Banknote className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-400">نقداً</span>
              </>
            )}
          </div>
          {order.notes && (
            <div className="flex-1 rounded-lg bg-surface p-2 border border-border/30">
              <p className="text-xs text-gray-500">ملاحظة:</p>
              <p className="text-sm text-gray-300 truncate">{order.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Items List - Scrollable with Fixed Max Height */}
      <div className="flex-1 overflow-hidden px-6 py-4">
        <h3 className="text-sm font-semibold text-gray-400 mb-3">
          المنتجات ({order.items.length})
        </h3>

        {/* Scrollable Items Container - THIS is the key fix! */}
        <div className="h-[calc(100vh-32rem)] overflow-y-auto rounded-lg border border-border/30 bg-surface-light">
          <div className="divide-y divide-border/30">
            {order.items.map((item, index) => (
              <div
                key={index}
                className="p-3 hover:bg-surface transition-colors"
              >
                <div className="flex items-center gap-3">
                  {/* Thumbnail - Smaller */}
                  <div className="w-10 h-10 rounded-lg bg-surface overflow-hidden shrink-0 border border-border/30">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <UtensilsCrossed className="h-5 w-5 text-gray-500" />
                      </div>
                    )}
                  </div>

                  {/* Item Info - Compact */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-white truncate">{item.nameAr}</span>
                      <span className="text-sm text-gray-400 shrink-0">
                        {(() => {
                          let price = item.unitPrice;
                          if (item.size) price = item.size.price;
                          if (item.variant) price += item.variant.priceModifier;
                          if (item.milk) price += item.milk.priceModifier;
                          return (price * item.quantity).toLocaleString('ar-IQ');
                        })()} د.ع
                      </span>
                    </div>

                    {/* Quantity */}
                    {item.quantity > 1 && (
                      <span className="text-xs text-gray-500">الكمية: {item.quantity}</span>
                    )}

                    {/* Options - Always show if present */}
                    {(item.size || item.variant || item.milk) && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {item.size && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            {item.size.nameAr}
                          </span>
                        )}
                        {item.variant && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            {item.variant.nameAr}
                          </span>
                        )}
                        {item.milk && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                            {item.milk.nameAr}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Notes - Always show if present */}
                    {item.notes && (
                      <p className="text-xs text-gray-500 mt-1 italic">{item.notes}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sticky Footer - Actions */}
      <div className="shrink-0 border-t border-border bg-surface-light px-6 py-4">
        {/* Error message */}
        {error && (
          <div className="mb-3 rounded-lg bg-red-500/20 px-4 py-2 text-red-400 border border-red-500/30 text-center text-sm">
            {error}
          </div>
        )}

        {/* Action Buttons - Prominent */}
        <div className="flex gap-3">
          <button
            onClick={handleSkip}
            disabled={isLoading}
            className="flex items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-6 py-3.5 font-medium text-red-400 transition-colors hover:bg-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {skipOrderMutation.isPending ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-red-400 border-t-transparent" />
            ) : (
              <X className="h-5 w-5" />
            )}
            <span>تخطي</span>
          </button>
          <button
            onClick={() => setShowModifyModal(true)}
            disabled={isLoading}
            className="flex items-center justify-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/10 px-6 py-3.5 font-medium text-blue-400 transition-colors hover:bg-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Pencil className="h-5 w-5" />
            <span>تعديل</span>
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-green-500 px-8 py-3.5 text-lg font-bold text-white transition-colors hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirmOrderMutation.isPending ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Check className="h-5 w-5" />
            )}
            <span>{order.paymentMethod === 'card' ? 'تأكيد' : 'تأكيد واستلام المبلغ'}</span>
          </button>
        </div>
      </div>

      {/* Modify Order Modal */}
      {showModifyModal && (
        <ModifyOrderModal
          order={order}
          shopId={shopId}
          onClose={() => setShowModifyModal(false)}
          onSave={handleModifySave}
          isSaving={updateOrderMutation.isPending}
        />
      )}
    </div>
  );
}

export default CurrentOrder;
